import React, { useState } from 'react'
import PropTypes from 'prop-types'
import {
  Box,
  TextField,
  Button,
  Typography,
  InputAdornment,
  CircularProgress,
} from '@mui/material'
import { LocalOffer as TagIcon } from '@mui/icons-material'
import { Cancel as XCircleIcon } from '@mui/icons-material'

export const CouponCodeInput = ({
  onApply,
  onCancel = () => {},
  placeholder = 'Enter coupon code',
  loading = false,
  error = null,
  success = null,
  disabled = false,
  ...props
}) => {
  const [couponCode, setCouponCode] = useState('')
  const [isExpanded, setIsExpanded] = useState(false)

  const handleInputChange = e => {
    setCouponCode(e.target.value.toUpperCase()) // Convert to uppercase for consistency
  }

  const handleApply = () => {
    if (couponCode.trim()) {
      onApply(couponCode.trim())
    }
  }

  const handleCancel = () => {
    setCouponCode('')
    setIsExpanded(false)
    onCancel()
  }

  const handleKeyDown = e => {
    if (e.key === 'Enter' && couponCode.trim()) {
      handleApply()
    }
    if (e.key === 'Escape') {
      handleCancel()
    }
  }

  const handleExpand = () => {
    setIsExpanded(true)
  }

  // If not expanded, show the collapsed state
  if (!isExpanded) {
    return (
      <Box
        sx={{
          alignItems: 'center',
          display: 'flex',
          cursor: 'pointer',
          p: 1,
          borderRadius: 1,
          border: '1px dashed',
          borderColor: 'primary.main',
          '&:hover': {
            backgroundColor: 'action.hover',
          },
        }}
        onClick={handleExpand}
      >
        <TagIcon fontSize="small" color="primary" />
        <Box sx={{ ml: 1 }}>
          <Typography color="primary" variant="body2">
            Have a coupon code?
          </Typography>
        </Box>
      </Box>
    )
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
        <TextField
          fullWidth
          size="small"
          placeholder={placeholder}
          value={couponCode}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          disabled={disabled || loading}
          error={!!error}
          autoFocus
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <TagIcon fontSize="small" color="action" />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                <XCircleIcon
                  fontSize="small"
                  onClick={handleCancel}
                  sx={{ cursor: 'pointer', color: 'action.secondary' }}
                />
              </InputAdornment>
            ),
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              '& fieldset': {
                borderColor: error ? 'error.main' : 'primary.main',
              },
            },
          }}
        />

        <Button
          variant="contained"
          size="small"
          onClick={handleApply}
          disabled={!couponCode.trim() || disabled || loading}
          sx={{ minWidth: 80, height: 40 }}
        >
          {loading ? <CircularProgress size={16} color="inherit" /> : 'Apply'}
        </Button>
      </Box>

      {/* Status messages */}
      {error && (
        <Typography
          variant="caption"
          color="error"
          sx={{ display: 'block', mt: 0.5, ml: 1 }}
        >
          {error}
        </Typography>
      )}

      {success && (
        <Typography
          variant="caption"
          color="success.main"
          sx={{ display: 'block', mt: 0.5, ml: 1 }}
        >
          {success}
        </Typography>
      )}
    </Box>
  )
}

CouponCodeInput.propTypes = {
  onApply: PropTypes.func.isRequired,
  onCancel: PropTypes.func,
  placeholder: PropTypes.string,
  loading: PropTypes.bool,
  error: PropTypes.string,
  success: PropTypes.string,
  disabled: PropTypes.bool,
}
